import React from "react";

const bounce = (marker: google.maps.marker.AdvancedMarkerElement, durationInMs = 2800) => {
  if (marker) {
    // @ts-expect-error google typing is off
    marker.content.classList.add("google-maps-animation-bounce");
    setTimeout(() => {
      // @ts-expect-error google typing is off
      marker.content.classList.remove("google-maps-animation-bounce");
    }, durationInMs);
  }
};

const bounceOnce = (marker: google.maps.marker.AdvancedMarkerElement) => bounce(marker, 1400);

const calculateNewZoomLevel = (currenZoom: number, targetZoom: number) => {
  if (Math.abs(currenZoom - targetZoom) <= 1) {
    return targetZoom;
  } else if (currenZoom < targetZoom) {
    return currenZoom + 1;
  } else {
    return currenZoom - 1;
  }
}

/**
 * Smoothly zooms the map to the target zoom level. This is needed as google doesn't do this by default.
 * Only small steps in the zoom level zoom smoothly.
 * 
 * @param map - Google map instance
 * @param targetZoom - target zoom level
 * @param callback - function called when the zoom is completed
 */
// This is currently unused, but we might want to use it in the future
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const smoothZoom = (map: google.maps.Map, targetZoom: number, callback: () => void) => {
  const currentZoom = map.getZoom();
  if (Math.abs(currentZoom - targetZoom) > 0.001) {
    const nextStepZoomLevel = calculateNewZoomLevel(currentZoom, targetZoom);
    const z = google.maps.event.addListener(map, "zoom_changed", () => {
      google.maps.event.removeListener(z);
      smoothZoom(map, targetZoom, callback);
    });
    setTimeout(() => map.setZoom(nextStepZoomLevel), 140);
  } else {
    callback();
  }
}

const panAndBounce = (map: google.maps.Map, marker: google.maps.marker.AdvancedMarkerElement) => {
  if (marker) {
    map.panTo(marker.position);
    bounce(marker);
  }
}

const select = (marker: google.maps.marker.AdvancedMarkerElement) => {
  if (marker) {
    // @ts-expect-error google typing is off
    marker.content.classList.add("google-maps-marker-selected");
  }
}

const deselect = (marker: google.maps.marker.AdvancedMarkerElement) => {
  if (marker) {
    // @ts-expect-error google typing is off
    marker.content.classList.remove("google-maps-marker-selected");
  }
}

const useSelectMarker = () => {
  const [_, setPreviousMarker] = React.useState<google.maps.marker.AdvancedMarkerElement>(undefined);

  return (marker: google.maps.marker.AdvancedMarkerElement) => {
    setPreviousMarker((state) => {
      if (state) {
        deselect(state);
      }

      select(marker);

      return marker;
    });
  };
};

// Apply the "selected" style on the hovered marker
const useHoverMarker = (selectedPosition: server.dto.Geo) => {
  const [_, setHoveredMarker] = React.useState<google.maps.marker.AdvancedMarkerElement>(undefined);

  return (newMarkerToHover: google.maps.marker.AdvancedMarkerElement) => {
    setHoveredMarker(prevHoveredMarker => {

      // If the marker is already selected (clicked), don't deselect it
      const prevMarkerIsSelectedMarker = selectedPosition != null
        && prevHoveredMarker != null
        && selectedPosition.lat === prevHoveredMarker.position.lat
        && selectedPosition.lng === prevHoveredMarker.position.lng;

      if (prevHoveredMarker && !prevMarkerIsSelectedMarker) {
        deselect(prevHoveredMarker);
      }

      if (newMarkerToHover) {
        select(newMarkerToHover);
      }

      return newMarkerToHover;
    })
  };
}

const polygonFadein = (polygon, seconds: number, opacity: number, callback?: () => void) => {
  polygon.setOptions({ fillOpacity: 0, strokeOpacity: 0 });
  const fill = 50 / (seconds * 999);

  const fadein = setInterval(() => {
    if ((polygon.get("fillOpacity") > opacity - 0.01)) {
      clearInterval(fadein);
      if (typeof (callback) === "function") {
        callback();
      }
      return;
    }

    polygon.setOptions({
      fillOpacity: Math.min(opacity, polygon.fillOpacity + fill)
    });
  }, 50);
};

const getGoogleUrl = (googlePlaceId: string, latLng: server.dto.Geo) => {
  if (!googlePlaceId && !latLng) {
    return null;
  }

  if (googlePlaceId) {
    return "https://www.google.com/maps/place/?q=place_id:" + googlePlaceId;
  }

  return `https://maps.google.com/?q=${latLng.lat},${latLng.lng}`;
};

export {
  bounce,
  bounceOnce,
  panAndBounce,
  useSelectMarker,
  polygonFadein,
  getGoogleUrl,
  useHoverMarker
};
